import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Save } from "react-feather";
import Swal from "sweetalert2";
import {
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { Btn, H5, P } from "../../../../../../AbstractElements";

// Services
import {
  genderService,
  VIEW_GENDER_QUERY_KEY,
} from "../../../../../../Services/genderService";

// Initial State
const INITIAL_STATE = {
  code: "",
  name: "",
  status: 1,
};

const CreateGenderForm = ({ toggle }: { toggle?: () => void }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(INITIAL_STATE);

  const createGenderMutation = useMutation({
    mutationFn: genderService.createGender,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: VIEW_GENDER_QUERY_KEY,
      });
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: "New gender record has been created successfully.",
        confirmButtonColor: "#4e73df",
      });
      setFormData(INITIAL_STATE);
      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message ||
        "Failed to create the gender record. Please try again.";
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "error",
        title: "Transaction Failed",
        text: message,
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    Swal.fire({
      title: "Create Gender Record?",
      text: "Are you sure you want to create this gender record?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: '<i class="fa fa-save"></i> Create New Record',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',
      confirmButtonColor: "#4e73df",
      cancelButtonColor: "#dc3545",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      Swal.fire({
        title: "Creating Gender Record...",
        text: "Please wait while the record is being created.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      createGenderMutation.mutate(formData);
    });
  };

  return (
    <ModalBody className="modal-body">
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>
        <Row className="g-3">
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <Row className="p-1">
              <Col md="5">
                <FormGroup>
                  <Label>
                    Code <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-social-slack txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="code"
                      name="code"
                      type="text"
                      maxLength={2}
                      minLength={1}
                      required
                      value={formData.code}
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="7">
                <FormGroup>
                  <Label>
                    Gender <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-info-circle txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      minLength={2}
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>
                    Status <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>

                    <Input
                      className="select-scroll"
                      value={formData.status}
                      id="status"
                      name="status"
                      type="select"
                      required
                      onChange={(e) => {
                        handleInputChange(e);
                      }}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>
          </div>

          <div className="d-flex justify-content-end">
            <Col md="6">
              <Btn
                color="primary"
                className="w-100"
                type="submit"
                disabled={createGenderMutation.isPending}
              >
                <Save size={16} className="me-1" />
                {createGenderMutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Btn>
            </Col>
          </div>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateGenderForm;
